package one.healthium.profile.ui.weight_history

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import loco.WEIGHT_HISTORY_SCREEN_TITLE
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.loco.resolve
import one.healthium.profile.ui.components.SwipeToDismissContainer
import one.healthium.profile.ui.weight.WeightHistoryUiModel
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppSeparator
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Instant

interface WeightHistoryEventDelegate {
    fun onBackClicked()
    fun onWeightHistoryClicked(weight: Float, date: Instant)
}

@Composable
fun WeightHistoryScreen(eventDelegate: WeightHistoryEventDelegate) {
    val viewModel = koinViewModel<WeightHistoryViewModel>()
    val state by viewModel.state.collectAsState()

    WeightHistoryScreenContent(
        state = state,
        onBackClicked = eventDelegate::onBackClicked,
        onWeightHistoryClicked = {
            eventDelegate.onWeightHistoryClicked(it.value, it.date)
        },
        onWeightHistoryDeleteClicked = viewModel::onWeightHistoryDeleteClicked,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
private fun WeightHistoryScreenContent(
    state: WeightHistoryUiState,
    onBackClicked: () -> Unit,
    onWeightHistoryClicked: (WeightHistoryUiModel) -> Unit,
    onWeightHistoryDeleteClicked: (WeightHistoryUiModel) -> Unit,
) {
    AppScaffold(
        topBar = {
            AppTopBar(
                title = {
                    Text(stringResource(loco.Res.string.WEIGHT_HISTORY_SCREEN_TITLE))
                },
                navigationIcon = {
                    AppBackNavigationButton(onClick = onBackClicked)
                }
            )
        }
    ) {
        Box(modifier = Modifier.padding(it)) {
            WightHistoryList(
                uiData = state.uiData,
                isMetric = state.isMetric,
                onWeightHistoryClicked = onWeightHistoryClicked,
                onWeightHistoryDeleteClicked = onWeightHistoryDeleteClicked,
            )

            AppLoadingScreen(visible = state.loading)
        }
    }
}

@Composable
private fun WightHistoryList(
    uiData: List<Any>?,
    isMetric: Boolean,
    onWeightHistoryClicked: (WeightHistoryUiModel) -> Unit,
    onWeightHistoryDeleteClicked: (WeightHistoryUiModel) -> Unit,
) {
    if (uiData == null) return

    LazyColumn(
        contentPadding = PaddingValues(start = 12.dp, end = 12.dp, bottom = 80.dp),
    ) {
        items(
            count = uiData.size,
            contentType = { index -> uiData[index]::class },
            key = { index -> uiData[index].key() },
        ) { index ->
            Spacer(Modifier.height(6.dp))

            when (val item = uiData[index]) {
                is WeightHistoryUiModel -> SwipeToDismissContainer(
                    modifier = Modifier.animateItem(),
                    onDelete = { onWeightHistoryDeleteClicked(item) }
                ) {
                    WithHistoryItem(
                        uiModel = item,
                        isMetric = isMetric,
                        onClick = { onWeightHistoryClicked(item) },
                    )
                }

                is String -> AppSeparator(
                    modifier = Modifier.animateItem(),
                    text = item,
                )
            }
        }
    }
}

@Composable
private fun WithHistoryItem(
    modifier: Modifier = Modifier,
    uiModel: WeightHistoryUiModel,
    isMetric: Boolean,
    onClick: () -> Unit,
) {
    Row(
        modifier = modifier
            .background(AppTheme.colors.surface, AppTheme.shapes.small)
            .clip(AppTheme.shapes.small)
            .clickable { onClick() }
            .padding(horizontal = 12.dp, vertical = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            modifier = Modifier.weight(1f),
            text = uiModel.dateFull,
            style = AppTheme.textStyles.secondary_14_400,
            color = AppTheme.colors.text100,
        )
        Spacer(Modifier.width(8.dp))
        Text(
            text = uiModel.value.formatWeight(isMetric).resolve(),
            style = AppTheme.textStyles.secondary_16_500,
            color = AppTheme.colors.text100,
        )
        Spacer(Modifier.width(8.dp))
        Image(
            modifier = Modifier.size(width = 8.dp, height = 13.dp),
            painter = painterResource(AppIcons.icArrowRight),
            contentDescription = null,
        )
    }
}

private fun Any.key(): Any = when (this) {
    is WeightHistoryUiModel -> this.id
    is String -> this
    else -> this
}